
import { Injectable, EventEmitter } from '@angular/core';
import { catchError, Observable, of, tap, throwError,take,map, EMPTY, shareReplay } from 'rxjs';
import { FavoriteChart } from 'src/app/models/favoriteChart';
import { ChartFolder } from 'src/app/models/chartFolder';
import { indexCalculatorInput } from 'src/app/models/indexCalculatorInput';
import { Contact } from 'src/app/models/contact';
import { Glossary } from 'src/app/models/glossary';
import { QuoteIfa } from 'src/app/models/quote-ifa';
import { HttpClient } from '@angular/common/http';
import { DeeperLookIcon } from 'src/app/models/deeperlookicon';
import { IndexDataBox } from 'src/app/models/data.ifa.com/indexDataBox';


type HtmlSnippets = {  
  index_fund_definition_sec_traditional$: () => Observable<any>;
  index_fund_definition_sec_non_traditional$: () => Observable<any>;  
}; 
@Injectable({
  providedIn: 'root'
})

export class Data2Service {
  //formToken is like a session variable. The server will validate the token on submission
  //declarative
 
  REFRESH_INTERVAL = 1000*60*60; //1hr. this value is in ms
  
  UserEmitter:EventEmitter<any> = new EventEmitter<any>();

  constructor(private http:HttpClient) { }

  
  returnsMonthly(query: string) {
    return this.http.get(`https://data2.ifa.com/odata/v1/returns/monthly/${query}`).pipe(
      catchError(err=>{return of("error returnsMonthly",err)})
  );
  }

  // ---- Index Data Boxes ----
  // Returns one databox per parent_id, in the same order requested. Backend caches ~10s;
  // each databox carries its live time-series-construction segments + definition html.
  // GET https://data2.ifa.com/api/v1/index-data-boxes?parent_ids=30441,30444,...
  indexDataBoxes$(parentIds: Array<number | string>): Observable<IndexDataBox[]> {
    const csv = (parentIds ?? []).join(',');
    const url = `https://data2.ifa.com/api/v1/index-data-boxes?parent_ids=${encodeURIComponent(csv)}`;
    return this.http.get<IndexDataBox[]>(url).pipe(
      map((data) => data ?? []),
      catchError((err) => { console.log('error indexDataBoxes', err); return of([] as IndexDataBox[]); })
    );
  }

  articlesCounts$(sitegroup:string='all'):Observable<any>{
    return this.http.get(`https://data2.ifa.com/odata/v1/articles/counts?sitegroup=${sitegroup}`).pipe(
      catchError(err=>{return of("error articlesCounts",err)})
  );
  }

  //prev/next navigation for articles ('all') and videos ('www.ifa.tv').
  //Server keeps a sorted per-sitegroup friendlyUrl list cached — replaces the old $top=1000 download.
  articleNavigation$(friendlyUrl: string, sitegroup: string = 'all'): Observable<{ previous: string, next: string }> {
    return this.http.get<{ previous: string | null, next: string | null }>(
      `https://data2.ifa.com/odata/v1/articles/${encodeURIComponent(friendlyUrl)}/navigation?sitegroup=${encodeURIComponent(sitegroup)}`
    ).pipe(
      map(nav => ({ previous: nav?.previous ?? '', next: nav?.next ?? '' })),
      catchError(err => { console.log('error articleNavigation', err); return of({ previous: '', next: '' }); })
    );
  }

  //decalrative
  articleVideoWhitelistCheck$(friendlyURL:string):Observable<boolean>{
    return this.http.get(`https://data2.ifa.com/odata/v1/articles/${friendlyURL}/whitelist`)
    .pipe(
      //tap((data:any)=>console.log("portfolio_underlying_mutual_funds$ tap",JSON.stringify(data))),
      map((resp)=>{   
        if(resp==true){
          //console.log(`${friendlyURL} is whitelisted`);
          return true;
        }       
        else{
          //console.log(`${friendlyURL} is NOT whitelisted`);
          return false;
        }
      }),
      //tap((mappedResp:any)=> console.log(mappedResp)),
      catchError(err => {
        console.log("There was an error getting 'Article / Video Whitelist' from the backend server. "+friendlyURL); 
        return of(false);
      }),
      shareReplay(120000) //2 min.
    );
  }

  becomeClientFormSubmit$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/form/become-client`,body).pipe(
      catchError(err=>{return of("error posting become-client",err)})
  );
  }

  chartsAlpha():Observable<any>{
    return this.http.get<any[]> ('https://data2.ifa.com/api/v1/alpha/charts');
  }

  FindAnAdvisorFormSubmit(body:any):Observable<any>
  {
    var url = "https://data2.ifa.com/api/v1/form/findanadvisor";
    // var url = "https://localhost:44396/api/v1/form/findanadvisor";
    return this.http.post(url, body)
      .pipe
      (
        catchError(err=>{return err}) // of("Bad",err)})
      // )
    );
  }

  EventRegistration_Add(body:any):Observable<any>
  {
    var url = "https://data2.ifa.com/api/v1/form/EventRegistration_Add";  
    // var url = "https://localhost:44396/api/v1/form/EventRegistration_Add";
    return this.http.post(url, body)
      .pipe
      (
        catchError(err=>{console.log(err); console.log('--body--'); console.log(body); return err}) // of("Bad",err)})
      );
  }
  
  consentLog$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/consent-log`,body).pipe(     
      //catchError(()=>of("error in contact options")),     
    );
  }

  //contact us form contacts
  //declarative
  contacts$(){ 
    return this.http.get<Contact[]>(`https://data2.ifa.com/odata/v1/contact?$select=name,dropdown`).pipe(     
      //catchError(()=>of("error in contact options")),
      shareReplay(this.REFRESH_INTERVAL)      
    );
  }
    
  contactUsFormSubmit$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/form/contact`,body).pipe(
      catchError(err=>{return of("error posting contact us",err)})
  );
  }

  deeperLookArticles() : Observable<DeeperLookIcon[]>{
    return this.http.get<DeeperLookIcon[]> ('https://data2.ifa.com/odata/v1/articles/deeperlooklist');
  }

  galtonboardContactUsFormSubmit$(body:any):Observable<any>{
    var apiurl = "https://data2.ifa.com/api/v1/form/galtonboardcontact"; 
    return this.http.post(apiurl, body).pipe(
      catchError(err=>{return of("error posting contact us",err)})
  );
  }

  //formToken is like a session variable. The server will validate the token on submission
  //declarative
  formToken$(){ 
      return  this.http.get<any>(`https://data2.ifa.com/api/v1/form/token`).pipe(
        catchError(err=>{return of("error getting form token")})
    );
  }

  fundOptionsFormSubmit$(body:any):Observable<any>{
      return this.http.post(`https://data2.ifa.com/api/v1/form/fund-options`,body).pipe(
        catchError(err=>{return of("error posting fund-options",err)})
    );
  }

  //declarative
  ifaOptimizedTargetDatesComparisonData$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/ifa-optimized-target-dates/comparison-data/`).pipe(
      map(x=>{
        //console.log("ifaOptimizedTargetDatesComparisonData$ > ",x)
        return x;

      }),
     catchError(
        err=>of("error getting ifaOptimizedTargetDateUnderlyingFundIds",err)
      )
    )
  }
  ifaOptimizedTargetDatesComparisonDataPOST(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/ifa-optimized-target-dates/comparison-data/`,body).pipe(
      map(x=>{
        //console.log("ifaOptimizedTargetDatesComparisonData$ > ",x)
        return x;

      }),
     catchError(
        err=>of("error getting ifaOptimizedTargetDateUnderlyingFundIds",err)
      )
    )
  }
  
  implementationAllocation$(implementationCode:string):Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/implementations/allocation/${implementationCode}`).pipe(catchError(err=>of([])))
  }

   implementationBenchmarkAllocation$(implementationCode:string, benchmarkIdType:number):Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/implementations/benchmark-allocation/${implementationCode}/${benchmarkIdType}`).pipe(catchError(err=>of([])))
  }

    
  implementationChoicesEquity$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/implementations/equity`).pipe(
      map((items:any) => Array.isArray(items) ? items.filter((x:any) => x.public === true) : items),
      catchError(err=>of([]))
    )
  }
  implementationChoicesFixedIncome$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/implementations/fixedincome`).pipe(
      map((items:any) => Array.isArray(items) ? items.filter((x:any) => x.public === true) : items),
      catchError(err=>of([]))
    )
  }
  implementationPresetMixes$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/implementations/preset-mixes`).pipe(catchError(err=>of([])))
  }




  //declarative 
  investment$(query:string=""):Observable<any>{
    return this.http.get(`https://data2.ifa.com/odata/v1/investment${query}`).pipe(catchError(err=>of([])));
  }

  investmentPerformanceDailyIndexGRR$(ids:string="",startDates:string,endDates:string,startingDollar:number=1,showIndexValues:boolean=true):Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/investment/performance/daily/index/grr?ids=${ids}&startDates=${startDates}&endDates=${endDates}&startingDollar=${startingDollar}&showIndexValues=${showIndexValues}`).pipe(catchError(err=>of([])))
  }
  investment_asset_classes$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/asset-classes`).pipe(catchError(err => { return of("error investment_classifications_asset_classes", err) }))  };
  investment_fund_types$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/fund-types`).pipe(catchError(err => { return of("error investment_classifications_fund_types", err) }))  };
  investment_managers$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/managers`).pipe(catchError(err => { return of("error investment_classifications_managers", err) }))  };
  investment_styles$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/styles`).pipe(catchError(err => { return of("error investment_classifications_styles", err) }))  };
  investment_sizes$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/sizes`).pipe(catchError(err => { return of("error investment_classifications_sizes", err) }))  };
  investment_screens$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/screens`).pipe(catchError(err => { return of("error investment_classifications_screens", err) }))  };
  investment_regions$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/regions`).pipe(catchError(err => { return of("error investment_classifications_regions", err) }))  };
  investment_maturities$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/maturities`).pipe(catchError(err => { return of("error investment_classifications_maturities", err) }))  };
  investment_security_types$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications/security-types`).pipe(catchError(err => { return of("error investment_classifications_security-types", err) }))  };
  investments_general_asset_classes$(): Observable < any > {  return this.http.get(`https://data2.ifa.com/odata/v1/investment/general-asset-classes`).pipe(catchError(err => { return of("error general-asset-classes", err) }))  };
  investment_classifications$(): Observable < any > {    
      return this.http.get(`https://data2.ifa.com/odata/v1/investment/classifications`).pipe(
        catchError(err => {
          return of("error investment_classifications", err)
        })
      )
    };




  openAccountFormSubmit$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/form/open-account`,body).pipe(
      catchError(err=>{return of("error posting open-account",err)})
  );
  }

  petitionCount$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/petition/count?id=5cc9f26f-c380-49ac-a888-c37542482228`).pipe(
      catchError(err=>{return of("error getting petition count",err)})
  );
  }
  petitionFormSubmit$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/petition/?id=5cc9f26f-c380-49ac-a888-c37542482228`,body).pipe(
      catchError(err=>{return of("error posting petition",err)})
  );
  }
  petitionTokenGet$():Observable<any>{
    return this.http.get(`https://data2.ifa.com/api/v1/petition/token`).pipe(
      catchError(err=>{return of("error posting petition token",err)})
  );
  }

  planSponsorChecklistFormSubmit$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/form/plan-sponsor-checklist`,body).pipe(
      catchError(err=>{return of("error posting plan-sponsor-checklist",err)})
  );
  }

 

  //declarative
  portfolio_underlying_mutual_funds$(portfolio:string):Observable<any>{
    return this.http.get(`https://data2.ifa.com/odata/v1/portfolio-underlying-mutual-funds?portfolio=${portfolio}`)
    .pipe(
      //tap((data:any)=>console.log("portfolio_underlying_mutual_funds$ tap",JSON.stringify(data))),
      map((resp:any)=>{  
        resp.data =  resp.data.map((x:any)=>{         
          x.specific_asset_class=x.general_asset_class_name?.replace(/[_]/g," ");   
          if(x.specific_asset_class){  
            x.general_asset_class_name = x.specific_asset_class
            .replace(/US (Large Cap|Large Value|Large Blend|Mid Cap Blend|Mid Cap Value|Mid Cap|Mid Value|Small Cap Value|Small Cap|Small Value|Small Blend|High Profitability|Core Equity 2)/g,"United States Equities")
            // .replace(/US (Large Cap|Large Value|Large Blend|)/g,"US Large")
            // .replace(/US (Mid Cap Blend|Mid Cap|Mid Value)/g,"US Mid")
            // .replace(/US (Small Cap|Small Value|Small Blend)/g,"US Small")
            .replace(/Global Real Estate/g,"Global Real Estate")
            .replace(/International (Blend|Small Cap Value|Small Cap|Small|Value|Sustainability Core|Social Core|Core Equity 2|Core|High Profitability)/g,"International Equities")
            .replace(/Emerging Markets (Blend|Small Cap|Small|Value|Sustainability Core|Social Core|Core Equity 2|Core|High Profitability)/g,"Emerging Markets Equities")
            .replace(/(One Year|Two Year|Five Year|Short Term Gov|Short Term|Investment Grade|Global Core|Sustainable|Social|Tax Exempt|Short Duration|US Core|Global ex US Core|Global Credit) Fixed Income/g,"Global Fixed Income")
          ;
          }   
          return x;
        });
        //console.log("resp.data", resp.data)
        return resp;
      }),
      //tap((mappedResp:any)=> console.log(mappedResp)),
      catchError(err => of("There was an error getting 'Implementations' from the backend server.",portfolio,err)),
      shareReplay(this.REFRESH_INTERVAL)
    );
  }
  
    //declarative
  index_calculator(indexCalculatorInput:indexCalculatorInput):Observable<any> { 
     return this.http.post<any>("https://data2.ifa.com/api/v1/calculators/index-calculator/",indexCalculatorInput)
    .pipe(
      //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),
      catchError(err => of("There was an error getting 'IFA Index Calculator Results' from the backend server.",indexCalculatorInput))
    );
  }

  public_get(){
    return this.http.get<any>("https://data2.ifa.com/public");
  }

  ///Interceptor will add auth header (MSALInterceptorConfigFactory)
  protected_get(){
    return this.http.get<any>("https://data2.ifa.com/protected");
  }

  ///Interceptor will add auth header (MSALInterceptorConfigFactory)
  dup_get(){     
      return this.http.get<any>("https://data2.ifa.com/api/v1/dup").pipe(
        shareReplay(2000)
      );       
  }

  fullyConsent$ = new Observable<boolean>();

  //declarative
  dup_get$(){ 
    return this.http.get<any>("https://data2.ifa.com/api/v1/dup")
    .pipe(
      //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),    
      catchError(err => of("There was an error getting user properties from the backend server.")),
      shareReplay(2000)
    );
  }

  dup_get_consent$(){ 
    return this.http.get<any>("https://data2.ifa.com/api/v1/dup").pipe(
      //tap(data=>console.log("dup_get_consent$ data",data)),   //DEBUG USING TAP
      map(resp=>{
        for(var i=0;i<resp.length;i++){
          resp[i].parts = resp[i].value.split(/[;|]+/);
        }
            console.log("dup_get_consent$",resp);
            console.log("dup_get_consent$",resp[0]);
            // console.log("dup_get_consent$",resp);
            // resp.parts = resp.value.split(/[;|]+/);
            // console.log("dup_get_consent$ (split)",resp);
            var readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions = resp.find((x: { parts: string[]; })=>x.parts[0]=="readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions");                
            var fullyConsented = readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions?.parts[1]=="Yes";
            console.log("fullyConsented?",fullyConsented, [],  readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions?.parts[1]=="Yes")
            
            if(fullyConsented){
              return {fullyConsented:true,consents:[readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions]};
            }
            else{
              return {fullyConsented:false,consents:[readAndUnderstoodIFAsBacktestingDisclosuresAndIndexDescriptions]};
            }          
      }),
      shareReplay(2000)
    ); 
  }

  ///
  ///Interceptor will add auth header (MSALInterceptorConfigFactory)
  ///dup_post(kv = delimited by '|')
  ///
  dup_post(kv:string){   
    console.log(Date.now+" - pushing to server:",kv);
     return this.http.post<any>("https://data2.ifa.com/api/v1/dup", {
        value:kv
      }).pipe(tap(x=>
        console.log(Date.now+" - pushed to server:",kv, x)
        ));
    }

  emailSubscribe(email:string):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/email/newslettersubscribe`,{email:email})   
  .pipe(
    //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),    
    catchError(err => of("There was an error subscribing to the newsletter."))
  );

  }

    //declarative
  html_snippets_ifa_disclosure$(){ 
    return this.http.get<any>("https://data2.ifa.com/odata/v1/html_snippets/?$filter=description eq 'IFA Disclosures'")
    .pipe(
      //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),       
      catchError(err => of("There was an error getting 'IFA Disclosures' the backend server."))
    );
  }

 

  htmlSnippets:HtmlSnippets = 
    {

      index_fund_definition_sec_non_traditional$: ()=>{
        return this.http.get<any>("https://data2.ifa.com/odata/v1/html_snippets/?$filter=description eq 'index-fund-definition-sec-non-traditional'")
        .pipe(
          //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),    
          catchError(err => of("There was an error getting 'index-fund-definition-sec-non-traditional' the backend server."))
        )
      }
      ,
      index_fund_definition_sec_traditional$: ()=>{ 
        return this.http.get<any>("https://data2.ifa.com/odata/v1/html_snippets/?$filter=description eq 'index-fund-definition-sec-traditional'")
          .pipe(
            //tap(data=>console.log("dub_get$ tap",JSON.stringify(data))),    
            catchError(err => of("There was an error getting 'index-fund-definition-sec-traditional' the backend server."))
          );    
      }
    }
  
    // getBooksNThumbnails$ = this.http.get<any[]>("https://localhost:44387/api/v1/books")
    //   .pipe
    //     (
    //       catchError(() => { return [];}),
    //       map((response: any[]) => {return response }),
    //       shareReplay(this.REFRESH_INTERVAL)
    //     );

  // testPipe(vcid: number = 0)
  chartview_gettotal$(vcid: number=0): Observable<any>
  { 
   var url = `https://data2.ifa.com/odata/v1/favoritecharts/gettotalviews?vcid=${vcid}`;
   return this.http.get<any[]>(url)
    .pipe
    (
      catchError((e) =>
      {
        return EMPTY;
      }),
      map((x: any[])=>
      { 
        var data = x;
        return data;
      })
    )
  }
  
  chartview_postincrement$(body: any): Observable<any>
  {
    var url = "https://data2.ifa.com/odata/v1/favoritecharts/viewincrement";
    return this.http.post<any>(url, body)
      .pipe
      (
        catchError((e) => { return EMPTY; }),
        map(() => {return EMPTY})
      )
  }

  //declarative
  favorite_charts$(){
    return this.http.get<FavoriteChart[]>("https://data2.ifa.com/odata/v1/favoriteCharts/v2")
    .pipe(
      //tap(data=>console.log("favorite_charts$ tap",JSON.stringify(data))),    
      catchError((e) => {
        console.error("There was an error getting 'Favorite Charts' the backend server."); 
      
        return EMPTY;
      }
        ),
      map((x:FavoriteChart[])=>{
        //console.log("data2service",x)
        var categories: ChartFolder[] = [];
        var catIndex = -1;
        for(var i=0;i<x.length;i++){
          if(catIndex==-1 || categories[catIndex].name != x[i].category){
            var cf =new ChartFolder();
            cf.name=x[i].category;
            categories.push(cf);
            catIndex++;         
          }
          x[i].index = i+1;
          x[i].id = Number.parseInt(x[i].imageUrl.split("/")[5]);        
        // https://data.ifa.com/content/charts/308/thumbnail_177w_100h_.png"

        // x[i].imageUrl="https://data.ifa.com/content/charts/154/thumbnail_177w_100h_.png?v=1"

          if(x[i].id==711){
            x[i].imageUrl='https://data.ifa.com/content/charts/711/8222/package/up.png';
            x[i].webLink='market-coin';
          }

          categories[catIndex].charts.push(x[i]);
          
        }
        return categories;
      }),
      shareReplay(this.REFRESH_INTERVAL)
    );
  }


 
  people$(query:string=""){   
    return this.http.get<any>(`https://data2.ifa.com/api/v1/people${query}`);
   }

   relatedContentByTags(tags:string=""){
    return this.http.get<any>(`https://data2.ifa.com/api/v1/tags?tags=${tags}`)
    .pipe(
      catchError(err=>{
          console.log(`Error getting related content for tags: ${tags}`);
          return of([]);
        }
      )
    );
   }

   
  //returns svg definitions for investments:
  //https://data2.ifa.com/api/v1/svgs/definitions?ids=696,697,695
  // svgsDefinitions(query:string){
  //    return this.http.get<any>(`https://data2.ifa.com/api/v1/svgs/definitions/${query}`);
  // }
   //returns svg definitions for investments:
  //https://data2.ifa.com/api/v1/svgs/definitions?ids=696,697,695
  svgsDefinitions(query:string){
    return this.http.get(`https://data2.ifa.com/api/v1/svgs/definitions/${query}`, { responseType: 'text' });
     //return this.http.get<any>(`https://data2.ifa.com/api/v1/svgs/definitions/${query}`, { responseType: 'text' });
  }

  timeSeriesConstructionCurrentAllocation(ids:string=""){
    return this.http.get<any>(`https://data2.ifa.com/odata/v1/time_series_construction/current-allocation/?ids=${ids}`);
   }

   taxEstimates(){
    return this.http.get<any>(`https://data2.ifa.com/api/v1/tax-estimates`);
   }

   

   videosByCategory$(query:string=""){
     return this.http.get<any>(`https://data2.ifa.com/odata/v1/videos/categories_v2${query}`);
   }

   
  videosByCategory_SP(query:string="")
  {
    return this.http.get<any>('https://data2.ifa.com/odata/v1/videos/sp');
  }

  videosFavorites$(query:string=""){
    return this.http.get<any>(`https://data2.ifa.com/odata/v1/videos/favorites${query}`).pipe(
      
     map((x:any)=>{
      //console.log("videosFavorites$ x",x)      
      let r =/https:\/\/services\.ifa\.com\/videos\/images\/(\d+)x(\d+)_c_([a-zA-Z0-9_-]+)\.(jpg|png)/

      for(var v=0;v<x.length;v++){
        let match = x[v].imageUrl.match(r);
        if(match){        
          x[v].imageUrl = `https://services.ifa.com/videos/images/${match[1]}x${match[2]}_c_${match[3]}.webp`
        }

        match = x[v].posterUrl.match(r);
        if(match){
          x[v].posterUrl = `https://services.ifa.com/videos/images/${match[1]}x${match[2]}_c_${match[3]}.webp`
        }

        for(var i=0;i<x[v].posters.length;i++){
          match = x[v].posters[i].url.match(r);
          if(match){         
            x[v].posters[i].url = `https://services.ifa.com/videos/images/${match[1]}x${match[2]}_c_${match[3]}.webp`
          }
        }
      }

      return x;

    }));
  }


  visitorLogSave$(body:any):Observable<any>{
    return this.http.post(`https://data2.ifa.com/api/v1/visitorlog`,body).pipe(
      catchError(err=>{return of("error posting visitor log us",err)})
  );
}


  videos$(query:string=""){
    return this.http.get<any>(`https://data2.ifa.com/odata/v1/videos/${query}`);
  }

   glossaryTermsOfArticle$(friendlyURL:string){
    //searches for terms in the content, title, keywords, description and teaser. returns alphabetically sorted array of glossary items
    return this.http.get<Glossary[]>(`https://data2.ifa.com/odata/v1/articles/${friendlyURL}/terms`);
  }
  relatedQuotesByTags$(tags:string){
    //comma seperated tags, searches for related quotes
    return this.http.get<QuoteIfa[]>(`https://data2.ifa.com/api/v1/tags/quotes/?tags=${tags}`).pipe(tap(x=>console.log("relatedQuotesByTags",x)));
  }

  
  chartFolders(){
    var url = 'https://data2.ifa.com/odata/v1/chart-folders'
    return this.http.get<any>(url);
  }
  chartsInFolders(query:string="?$expand=cmS_HTML5_Chart"){
    var url = `https://data2.ifa.com/odata/v1/charts-in-folders${query}`
    return this.http.get<any>(url);
  }

  getInstitutionalArticles() : Observable<any[]> {
    return this.http.get<any[]> ('https://data2.ifa.com/odata/v1/articles/?$filter=sitegroup%20eq%20%27www.ifainstitutional.com-articles%27&$orderBy=date desc');
  }

  getVideos() : Observable<any[]> {
    return this.http.get<any[]> ('https://data2.ifa.com/api/v1/tags?tags=ifainstitutional.com');  
  }

  getInstitutionalVideos() : Observable<any[]> {
    return this.http.get<any[]> ('https://data2.ifa.com/odata/v1/articles/?$filter=sitegroup%20eq%20%27www.ifainstitutional.com-videos%27&$orderBy=date desc');  
  }
  
}
